import { useState, useEffect } from "react";
import { getYtdReport, getQuarterlyReport, exportData, getMonthName, getQuarterLabel } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { 
  Download, 
  Calendar, 
  TrendingUp,
  BarChart3,
  FileSpreadsheet,
  Loader2,
  Building2
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const YEARS = [2024, 2025, 2026, 2027];
const QUARTERS = [
  { value: 1, label: "Q1 (Jan-Mar)" },
  { value: 2, label: "Q2 (Apr-Jun)" },
  { value: 3, label: "Q3 (Jul-Sep)" },
  { value: 4, label: "Q4 (Oct-Dec)" },
];

export default function Reports() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [loading, setLoading] = useState(false);
  const [ytdData, setYtdData] = useState(null);
  const [quarterlyData, setQuarterlyData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    const fetchYtd = async () => {
      setLoading(true);
      try {
        const result = await getYtdReport(year);
        setYtdData(result);
      } catch (error) {
        console.error("Failed to fetch YTD report:", error);
      }
      setLoading(false);
    };
    fetchYtd();
  }, [year]);

  useEffect(() => {
    const fetchQuarterly = async () => {
      try {
        const result = await getQuarterlyReport(year, quarter);
        setQuarterlyData(result);
      } catch (error) {
        console.error("Failed to fetch quarterly report:", error);
      }
    };
    fetchQuarterly();
  }, [year, quarter]);

  // Fetch logo
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/settings/logo`);
        const data = await res.json();
        if (data.exists && data.data) {
          setLogoUrl(`data:${data.content_type};base64,${data.data}`);
        }
      } catch (error) {
        console.error("Failed to fetch logo:", error);
      }
    };
    fetchLogo();
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportData(year);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `infection_control_${year}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Exported ${year} data`);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export data. Make sure there is data for this year.");
    }
    setExporting(false);
  };

  const goals = ytdData?.goals || {};
  const monthlyRates = ytdData?.monthly_rates || [];

  // Status helper
  const getStatusClass = (value, goal, inverse = false) => {
    if (goal === undefined || goal === null) return "text-slate-900";
    if (inverse) {
      if (value >= goal) return "text-emerald-600";
      if (value >= goal * 0.9) return "text-amber-600";
      return "text-red-600";
    } else {
      if (value <= goal) return "text-emerald-600";
      if (value <= goal * 1.2) return "text-amber-600";
      return "text-red-600";
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          {logoUrl ? (
            <img 
              src={logoUrl} 
              alt="Organization Logo" 
              className="h-12 w-auto max-w-[160px] object-contain"
              data-testid="reports-logo"
            />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#00B4C5] to-[#6A26B5] flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Reports
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Monthly, quarterly, and year-to-date reports
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="report-year-selector">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {YEARS.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={exporting}
            className="border-sky-200 text-sky-700 hover:bg-sky-50"
            data-testid="export-data-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export CSV
          </Button>
        </div>
      </div>

      <Tabs defaultValue="ytd" className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="ytd" className="data-[state=active]:bg-white" data-testid="tab-ytd">
            <TrendingUp className="w-4 h-4 mr-2" />
            Year-to-Date
          </TabsTrigger>
          <TabsTrigger value="quarterly" className="data-[state=active]:bg-white" data-testid="tab-quarterly">
            <Calendar className="w-4 h-4 mr-2" />
            Quarterly
          </TabsTrigger>
          <TabsTrigger value="monthly" className="data-[state=active]:bg-white" data-testid="tab-monthly">
            <BarChart3 className="w-4 h-4 mr-2" />
            Monthly Detail
          </TabsTrigger>
        </TabsList>

        {/* YTD Tab */}
        <TabsContent value="ytd" className="space-y-6">
          {/* YTD Summary Table */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <FileSpreadsheet className="w-5 h-5 text-sky-600" />
                {year} Year-to-Date Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Metric</TableHead>
                      <TableHead className="font-semibold text-right">YTD Value</TableHead>
                      <TableHead className="font-semibold text-right">Goal</TableHead>
                      <TableHead className="font-semibold text-right">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[
                      { label: "CAUTI Rate (/1K cath days)", value: ytdData?.rates?.cauti_rate, goal: goals.cauti_rate },
                      { label: "CLABSI Rate (/1K line days)", value: ytdData?.rates?.clabsi_rate, goal: goals.clabsi_rate },
                      { label: "C.diff Rate (/10K pt days)", value: ytdData?.rates?.cdiff_rate, goal: goals.cdiff_rate },
                      { label: "MDRO Rate (/1K pt days)", value: ytdData?.rates?.mdro_rate, goal: goals.mdro_rate },
                      { label: "Total HAI Rate (/1K pt days)", value: ytdData?.rates?.total_hai_rate, goal: goals.total_hai_rate },
                      { label: "Hand Hygiene Compliance (%)", value: ytdData?.rates?.hand_hygiene_rate, goal: goals.hand_hygiene_compliance, inverse: true },
                      { label: "Isolation PPE Compliance (%)", value: ytdData?.rates?.isolation_ppe_rate, goal: goals.isolation_ppe_compliance, inverse: true },
                      { label: "IC EOC Compliance (%)", value: ytdData?.rates?.ic_eoc_rate, goal: goals.ic_eoc_compliance, inverse: true },
                      { label: "Catheter Utilization Ratio", value: ytdData?.rates?.urinary_catheter_utilization, goal: goals.urinary_catheter_utilization },
                      { label: "Central Line Utilization Ratio", value: ytdData?.rates?.central_line_utilization, goal: goals.central_line_utilization },
                    ].map((row, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{row.label}</TableCell>
                        <TableCell className={`text-right font-mono ${getStatusClass(row.value, row.goal, row.inverse)}`}>
                          {row.value?.toFixed(2) ?? '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono text-slate-500">
                          {row.inverse ? '≥' : '≤'}{row.goal ?? '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.value !== undefined && row.goal !== undefined && (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              (row.inverse ? row.value >= row.goal : row.value <= row.goal)
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {(row.inverse ? row.value >= row.goal : row.value <= row.goal) ? 'Met' : 'Not Met'}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* YTD Trends */}
          {monthlyRates.length > 0 && (
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Infection Rate Trends
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={monthlyRates}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month_name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                    <Legend />
                    <Line type="monotone" dataKey="cauti_rate" name="CAUTI" stroke="#0284c7" strokeWidth={2} />
                    <Line type="monotone" dataKey="clabsi_rate" name="CLABSI" stroke="#059669" strokeWidth={2} />
                    <Line type="monotone" dataKey="cdiff_rate" name="C.diff" stroke="#f59e0b" strokeWidth={2} />
                    <Line type="monotone" dataKey="total_hai_rate" name="Total HAI" stroke="#ef4444" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Quarterly Tab */}
        <TabsContent value="quarterly" className="space-y-6">
          <div className="flex gap-3 mb-4">
            <Select value={String(quarter)} onValueChange={(v) => setQuarter(Number(v))}>
              <SelectTrigger className="w-40" data-testid="quarter-selector">
                <SelectValue placeholder="Quarter" />
              </SelectTrigger>
              <SelectContent>
                {QUARTERS.map((q) => (
                  <SelectItem key={q.value} value={String(q.value)}>{q.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {quarterlyData && (
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {getQuarterLabel(quarter)} {year} Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">Patient Days</p>
                    <p className="text-xl font-bold font-mono">{quarterlyData.stats?.patient_days || 0}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">Admissions</p>
                    <p className="text-xl font-bold font-mono">{quarterlyData.stats?.admissions || 0}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">Total HAIs</p>
                    <p className="text-xl font-bold font-mono">{quarterlyData.rates?.total_hai_count || 0}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase">HAI Rate</p>
                    <p className="text-xl font-bold font-mono">{quarterlyData.rates?.total_hai_rate || 0}</p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Metric</TableHead>
                        <TableHead className="text-right">Q{quarter} Value</TableHead>
                        <TableHead className="text-right">Goal</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[
                        { label: "CAUTI Rate", value: quarterlyData.rates?.cauti_rate, goal: quarterlyData.goals?.cauti_rate },
                        { label: "CLABSI Rate", value: quarterlyData.rates?.clabsi_rate, goal: quarterlyData.goals?.clabsi_rate },
                        { label: "Hand Hygiene %", value: quarterlyData.rates?.hand_hygiene_rate, goal: quarterlyData.goals?.hand_hygiene_compliance, inverse: true },
                        { label: "Isolation PPE %", value: quarterlyData.rates?.isolation_ppe_rate, goal: quarterlyData.goals?.isolation_ppe_compliance, inverse: true },
                      ].map((row, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{row.label}</TableCell>
                          <TableCell className={`text-right font-mono ${getStatusClass(row.value, row.goal, row.inverse)}`}>
                            {row.value?.toFixed(2) ?? '-'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-slate-500">
                            {row.inverse ? '≥' : '≤'}{row.goal ?? '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Monthly Detail Tab */}
        <TabsContent value="monthly" className="space-y-6">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Monthly Breakdown - {year}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Month</TableHead>
                      <TableHead className="text-right">Pt Days</TableHead>
                      <TableHead className="text-right">CAUTI</TableHead>
                      <TableHead className="text-right">CLABSI</TableHead>
                      <TableHead className="text-right">C.diff</TableHead>
                      <TableHead className="text-right">Total HAIs</TableHead>
                      <TableHead className="text-right">HH %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ytdData?.monthly_data?.map((month, i) => {
                      const rates = monthlyRates[i] || {};
                      return (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{getMonthName(month.month)}</TableCell>
                          <TableCell className="text-right font-mono">{month.patient_days}</TableCell>
                          <TableCell className="text-right font-mono">{month.cauti_count}</TableCell>
                          <TableCell className="text-right font-mono">{month.clabsi_count}</TableCell>
                          <TableCell className="text-right font-mono">{month.cdiff_count}</TableCell>
                          <TableCell className="text-right font-mono">{rates.total_hai_count || 0}</TableCell>
                          <TableCell className={`text-right font-mono ${getStatusClass(rates.hand_hygiene_rate, 90, true)}`}>
                            {rates.hand_hygiene_rate?.toFixed(1) || '-'}%
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {(!ytdData?.monthly_data || ytdData.monthly_data.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                          No data available for {year}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Bar Chart */}
          {monthlyRates.length > 0 && (
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Monthly Patient Days & Admissions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={ytdData?.monthly_data || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="month" 
                      tickFormatter={(m) => getMonthName(m)?.substring(0, 3)} 
                      tick={{ fontSize: 11 }} 
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip 
                      labelFormatter={(m) => getMonthName(m)}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} 
                    />
                    <Legend />
                    <Bar dataKey="patient_days" name="Patient Days" fill="#0284c7" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="admissions" name="Admissions" fill="#059669" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
